import { Checkbox, Stack, Typography } from "@mui/material";
import type { GridRenderCellParams, GridRowId } from "@mui/x-data-grid-premium";
import { gridRowSelectionIdsSelector, useGridApiContext, useGridSelector } from "@mui/x-data-grid-premium";
import { useCallback } from "react";
import type { AccessPermission } from "@entrilia/shared/api/types";
import AuthorizedBox from "@entrilia/shared/components/AuthorizedBox";
import { ExpandCollapseButton } from "../components/ExpandCollapseButton.tsx";

export default function GroupName({
  params,
  permissions,
  onExpandToggle,
}: {
  params: GridRenderCellParams;
  permissions: AccessPermission[];
  onExpandToggle: (expanded: boolean, rowId: GridRowId) => void;
}) {
  const apiRef = useGridApiContext();
  const selectedIds = useGridSelector(apiRef, gridRowSelectionIdsSelector);

  const changeSelection = useCallback(
    (newState: boolean) => {
      const rows = apiRef.current.getRowGroupChildren({ groupId: params.id });
      apiRef.current.selectRows(rows, newState);
    },
    [apiRef, params.id]
  );

  if (params.rowNode.type !== "group") {
    return null;
  }

  const childRows = params.rowNode.children;
  const selectedChildren = childRows.filter((child) => selectedIds.has(child)).length;
  const allChildrenSelected = childRows.length > 0 && childRows.length === selectedChildren;
  const selected = allChildrenSelected || selectedIds.has(params.id);
  const expanded = params.rowNode.childrenExpanded === true;

  return (
    <Stack direction="row" sx={{ alignItems: "center" }}>
      <AuthorizedBox permissions={permissions}>
        <Checkbox
          indeterminate={selectedChildren > 0 && !allChildrenSelected}
          checked={selected}
          onChange={(_, checked) => changeSelection(checked)}
        />
      </AuthorizedBox>
      <ExpandCollapseButton
        expanded={expanded}
        onClick={(value) => {
          apiRef.current?.setRowChildrenExpansion(params.id, value);
          onExpandToggle(value, params.id);
        }}
      />
      <Typography variant="subtitle2" sx={{ pl: 1 }}>
        {params.value} ({params.rowNode.children.length})
      </Typography>
    </Stack>
  );
}
